<template>
    <section class="student-payment-detail">
        <div class="container student-payment-detail__container">
            <StudentSidebar :student="student" />

            <main class="student-payment-detail__main">
                <header class="student-payment-detail__topbar">
                    <div>
                        <span class="student-payment-detail__eyebrow">Payments</span>
                        <h1>Payment Details</h1>
                        <p>Review the full payment record for this course, including payment method, transaction information, coupon savings, invoice, and access expiry.</p>
                    </div>

                    <button type="button" class="student-payment-detail__back" @click="goBack">
                        <i class="ri-arrow-left-line"></i>
                        Back
                    </button>
                </header>

                <section class="student-payment-detail__hero">
                    <div class="student-payment-detail__course-card">
                        <div class="student-payment-detail__course-media">
                            <img :src="paymentCourse.thumbnailPath" :alt="paymentCourse.title" />
                        </div>

                        <div class="student-payment-detail__course-body">
                            <div class="student-payment-detail__course-head">
                                <div>
                                    <span class="student-payment-detail__eyebrow">Course Details</span>
                                    <h2>{{ paymentCourse.title }}</h2>
                                </div>

                                <span class="student-payment-detail__status" :class="`is-${paymentCourse.statusTone || 'progress'}`">
                                    {{ paymentCourse.statusLabel }}
                                </span>
                            </div>

                            <p>Payment records for this course are shown below in a clean timeline so the student can easily review purchase date, amount, method, and access expiry.</p>

                            <div class="student-payment-detail__hero-actions">
                                <a :href="paymentCourse.learningUrl || paymentCourse.browseUrl" class="student-payment-detail__primary-btn">
                                    {{ paymentCourse.learningUrl ? 'Open Course' : 'Browse Course' }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="student-payment-detail__summary">
                        <article class="student-payment-detail__summary-card">
                            <span>Total Payments</span>
                            <strong>{{ paymentCourse.paymentCount }}</strong>
                        </article>

                        <article class="student-payment-detail__summary-card">
                            <span>Last Paid</span>
                            <strong>{{ paymentCourse.currency }} {{ money(paymentCourse.latestPaidAmount) }}</strong>
                        </article>

                        <article class="student-payment-detail__summary-card">
                            <span>Coupon Savings</span>
                            <strong>{{ paymentCourse.currency }} {{ money(totalCouponAmount) }}</strong>
                        </article>

                        <article class="student-payment-detail__summary-card student-payment-detail__summary-card--expiry">
                            <span>Access Expires</span>
                            <strong>{{ paymentCourse.latestExpireDateLabel || 'Not set' }}</strong>
                        </article>
                    </div>
                </section>

                <section class="student-payment-detail__panel">
                    <div class="student-payment-detail__panel-head">
                        <div>
                            <span class="student-payment-detail__eyebrow">History timeline</span>
                            <h2>Payment Records</h2>
                        </div>

                        <p>{{ paymentCourse.records.length }} invoice{{ paymentCourse.records.length === 1 ? '' : 's' }}</p>
                    </div>

                    <div class="student-payment-detail__history">
                        <article
                            v-for="record in paymentCourse.records"
                            :key="record.id"
                            class="student-payment-detail__history-card"
                        >
                            <div class="student-payment-detail__history-top">
                                <div>
                                    <span class="student-payment-detail__mini-label">Invoice</span>
                                    <h3>{{ record.invoiceNumber }}</h3>
                                    <small>{{ record.orderStatusLabel }}</small>
                                </div>

                                <div class="student-payment-detail__history-top-right">
                                    <strong>{{ record.currency }} {{ money(record.paidAmount) }}</strong>
                                    <span class="student-payment-detail__status" :class="`is-${record.statusTone || 'progress'}`">
                                        {{ record.statusLabel }}
                                    </span>
                                </div>
                            </div>

                            <div class="student-payment-detail__history-grid">
                                <div>
                                    <span>Purchased On</span>
                                    <strong>{{ record.purchaseDateLabel || '-' }}</strong>
                                    <small>{{ record.purchaseTimeLabel || '-' }}</small>
                                </div>

                                <div>
                                    <span>Payment Date</span>
                                    <strong>{{ record.paymentDateLabel || record.purchaseDateLabel || '-' }}</strong>
                                    <small>{{ record.orderStatusLabel }}</small>
                                </div>

                                <div>
                                    <span>Payment Method</span>
                                    <strong>{{ record.paymentMethod || 'Online Payment' }}</strong>
                                    <small>{{ record.paymentMethodMeta || 'Processed via SSLCommerz' }}</small>
                                </div>

                                <div>
                                    <span>Transaction ID</span>
                                    <strong>{{ record.transactionId || '-' }}</strong>
                                    <small>{{ record.paymentGateway || 'SSLCommerz' }}</small>
                                </div>

                                <div>
                                    <span>Course Price</span>
                                    <strong>{{ record.currency }} {{ money(record.subtotal) }}</strong>
                                    <small>Final {{ record.currency }} {{ money(record.finalAmount || record.paidAmount) }}</small>
                                </div>

                                <div>
                                    <span>Net Paid</span>
                                    <strong>{{ record.currency }} {{ money(record.paidAmount) }}</strong>
                                    <small>{{ couponText(record) }}</small>
                                </div>

                                <div>
                                    <span>Access Started</span>
                                    <strong>{{ record.accessStartLabel || '-' }}</strong>
                                    <small>Enrollment based access start</small>
                                </div>

                                <div>
                                    <span>Access Expires</span>
                                    <strong>{{ record.expireDateLabel || 'Not set' }}</strong>
                                    <small>{{ record.statusLabel }}</small>
                                </div>
                            </div>
                        </article>
                    </div>
                </section>
            </main>
        </div>
    </section>
</template>

<script setup>
import { computed } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentPaymentHistoryDetailComponent');

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const paymentCourse = parseJson(root?.dataset.paymentCourse, {});

const totalCouponAmount = computed(() => {
    return (paymentCourse.records || []).reduce((sum, record) => sum + Number(record.couponAmount || 0), 0);
});

function couponText(record) {
    if (Number(record.couponAmount || 0) > 0) {
        return `Coupon ${record.couponCode || '-'} saved ${record.currency} ${money(record.couponAmount)}`;
    }

    return 'No coupon used';
}

function money(value) {
    const amount = Number(value || 0);

    return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function goBack() {
    if (window.history.length > 1) {
        window.history.back();
        return;
    }

    window.location.href = '/student/payment-history';
}
</script>

<style scoped>
.student-payment-detail {
    background:
        radial-gradient(circle at top left, rgba(13, 110, 253, 0.08), transparent 32%),
        linear-gradient(180deg, #f5f8fc 0%, #eef2f7 52%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-payment-detail__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-payment-detail__main {
    min-width: 0;
}

.student-payment-detail__topbar,
.student-payment-detail__hero,
.student-payment-detail__panel {
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.92);
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-payment-detail__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
    background:
        linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.05)),
        #ffffff;
    border-color: #dcecff;
}

.student-payment-detail__topbar h1,
.student-payment-detail__course-head h2,
.student-payment-detail__panel-head h2,
.student-payment-detail__history-top h3 {
    margin: 0;
    color: #111827;
    font-weight: 800;
}

.student-payment-detail__topbar h1 {
    font-size: 24px;
}

.student-payment-detail__topbar p,
.student-payment-detail__course-body p,
.student-payment-detail__panel-head p {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.6;
}

.student-payment-detail__eyebrow,
.student-payment-detail__mini-label {
    display: block;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
}

.student-payment-detail__eyebrow {
    margin-bottom: 4px;
}

.student-payment-detail__mini-label {
    margin-bottom: 6px;
}

.student-payment-detail__back,
.student-payment-detail__primary-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
    transition: all 0.2s ease;
}

.student-payment-detail__back {
    border: 0;
    gap: 8px;
    background: #e7f1ff;
    color: #0d6efd;
}

.student-payment-detail__primary-btn {
    background: #0d6efd;
    color: #ffffff;
}

.student-payment-detail__back:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-payment-detail__primary-btn:hover {
    background: #0b5ed7;
    color: #ffffff;
    transform: translateY(-1px);
}

.student-payment-detail__hero {
    display: grid;
    gap: 18px;
    padding: 22px;
    margin-bottom: 18px;
}

.student-payment-detail__course-card {
    display: grid;
    grid-template-columns: 220px minmax(0, 1fr);
    gap: 18px;
    align-items: stretch;
}

.student-payment-detail__course-media {
    overflow: hidden;
    border-radius: 8px;
    background: #eff6ff;
}

.student-payment-detail__course-media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.student-payment-detail__course-body {
    display: grid;
    gap: 14px;
    align-content: center;
}

.student-payment-detail__course-head,
.student-payment-detail__history-top,
.student-payment-detail__panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
}

.student-payment-detail__course-head h2 {
    font-size: 24px;
}

.student-payment-detail__hero-actions {
    display: flex;
    gap: 10px;
}

.student-payment-detail__summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 14px;
}

.student-payment-detail__summary-card {
    padding: 16px;
    border: 1px solid #e7eef8;
    border-radius: 8px;
    background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.student-payment-detail__summary-card--expiry {
    border-color: #fecaca;
    background: linear-gradient(180deg, #fff7f7 0%, #fef2f2 100%);
}

.student-payment-detail__summary-card span,
.student-payment-detail__history-grid span {
    display: block;
    color: #64748b;
    font-size: 12px;
    font-weight: 700;
}

.student-payment-detail__summary-card strong,
.student-payment-detail__history-grid strong,
.student-payment-detail__history-top strong {
    display: block;
    margin-top: 5px;
    color: #111827;
    font-size: 20px;
    font-weight: 900;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.student-payment-detail__summary-card--expiry span,
.student-payment-detail__summary-card--expiry strong {
    color: #dc2626;
}

.student-payment-detail__panel {
    padding: 22px;
}

.student-payment-detail__history {
    display: grid;
    gap: 14px;
}

.student-payment-detail__history-card {
    padding: 18px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
}

.student-payment-detail__history-top {
    padding-bottom: 16px;
    margin-bottom: 16px;
    border-bottom: 1px solid #eef2f7;
}

.student-payment-detail__history-top h3 {
    font-size: 20px;
}

.student-payment-detail__history-top small,
.student-payment-detail__history-grid small {
    display: block;
    margin-top: 4px;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 700;
}

.student-payment-detail__history-top-right {
    display: grid;
    justify-items: end;
    gap: 8px;
    min-width: 0;
}

.student-payment-detail__history-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px 18px;
}

.student-payment-detail__status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 28px;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 800;
    white-space: nowrap;
}

.student-payment-detail__status.is-progress,
.student-payment-detail__status.is-active,
.student-payment-detail__status.is-continue {
    background: #eff6ff;
    color: #2563eb;
}

.student-payment-detail__status.is-completed {
    background: #ecfdf5;
    color: #047857;
}

.student-payment-detail__status.is-expired {
    background: #fef2f2;
    color: #dc2626;
}

@media (max-width: 1199px) {
    .student-payment-detail__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }

    .student-payment-detail__summary,
    .student-payment-detail__history-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 991px) {
    .student-payment-detail {
        padding: 24px 0 36px;
    }

    .student-payment-detail__container,
    .student-payment-detail__course-card {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .student-payment-detail__topbar,
    .student-payment-detail__course-head,
    .student-payment-detail__history-top,
    .student-payment-detail__panel-head {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-payment-detail__history-top-right {
        justify-items: start;
    }

    .student-payment-detail__summary,
    .student-payment-detail__history-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 575px) {
    .student-payment-detail__container {
        padding: 0 10px;
    }

    .student-payment-detail__topbar,
    .student-payment-detail__hero,
    .student-payment-detail__panel,
    .student-payment-detail__history-card {
        padding: 16px;
    }

    .student-payment-detail__back,
    .student-payment-detail__primary-btn {
        width: 100%;
    }
}
</style>
